<script setup>
import { computed, ref } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { intlLocale, useLocoI18n } from 'custom/composables/useLocoI18n';
import { describeToolCall, formatCost, formatTokens, safeUrl } from './helpers';

// What an AI answer was based on and what it did: knowledge sources, tool calls (handoff and its reason included),
// model, tokens and (for admins) cost.
// Collapsed to one line until clicked.
const props = defineProps({
  sources: { type: Array, default: () => [] },
  toolCalls: { type: Array, default: () => [] },
  tokens: { type: Object, default: null },
  model: { type: String, default: '' },
  cost: { type: Number, default: null },
});

const { t, locale } = useLocoI18n();
const { isAdmin } = useAdmin();
const isOpen = ref(false);

const steps = computed(() => props.toolCalls.map(describeToolCall));
const usageLine = computed(() =>
  [
    props.model,
    props.tokens &&
      t('LOCO.AI.TRACE.TOKENS', {
        input: formatTokens(props.tokens.input, intlLocale(locale.value)),
        output: formatTokens(props.tokens.output, intlLocale(locale.value)),
      }),
    isAdmin.value &&
      props.cost !== null &&
      formatCost(props.cost, intlLocale(locale.value)),
  ]
    .filter(Boolean)
    .join(' · ')
);
const summary = computed(() =>
  [
    t('LOCO.AI.TRACE.AI'),
    props.sources.length && t('LOCO.AI.TRACE.SOURCES', props.sources.length),
    steps.value.length && t('LOCO.AI.TRACE.ACTIONS', steps.value.length),
  ]
    .filter(Boolean)
    .join(' · ')
);
</script>

<template>
  <div class="flex flex-col gap-1 text-xs text-n-slate-11 max-w-md">
    <button
      type="button"
      class="self-start flex items-center gap-1 hover:text-n-slate-12"
      @click="isOpen = !isOpen"
    >
      <Icon icon="i-lucide-sparkles" class="size-3 text-n-iris-11" />
      {{ summary }}
      <Icon
        :icon="isOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
        class="size-3"
      />
    </button>
    <div
      v-if="isOpen"
      class="flex flex-col gap-2 p-2 rounded-lg bg-n-alpha-1 text-n-slate-11"
    >
      <div v-if="sources.length" class="flex flex-col gap-0.5">
        <span class="font-medium text-n-slate-12">
          {{ t('LOCO.AI.TRACE.KNOWLEDGE') }}
        </span>
        <component
          :is="source.url ? 'a' : 'span'"
          v-for="(source, index) in sources"
          :key="index"
          :href="safeUrl(source.url)"
          target="_blank"
          rel="noopener noreferrer"
          class="truncate"
          :class="{ 'text-n-blue-11 hover:underline': source.url }"
        >
          {{ source.title || source.url || t('LOCO.AI.TRACE.UNTITLED') }}
        </component>
      </div>
      <div v-if="steps.length" class="flex flex-col gap-0.5">
        <span class="font-medium text-n-slate-12">
          {{ t('LOCO.AI.TRACE.DID') }}
        </span>
        <span
          v-for="(step, index) in steps"
          :key="index"
          class="flex items-start gap-1"
        >
          <Icon :icon="step.icon" class="size-3 mt-0.5 flex-shrink-0" />
          <span>
            {{ t(`LOCO.AI.TRACE.STEP.${step.key}`, { detail: step.detail }) }}
          </span>
        </span>
      </div>
      <span v-if="!sources.length && !steps.length">
        {{ t('LOCO.AI.TRACE.NO_KNOWLEDGE') }}
      </span>
      <span v-if="usageLine" class="text-n-slate-10">{{ usageLine }}</span>
    </div>
  </div>
</template>
